// src/components/userComponents/UserEdit.tsx
import React, { useState, useEffect } from 'react';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { getUserById, updateUser } from '../../services/fusionAuthService';
import './UserEdit.scss';

const E164 = /^\+[1-9]\d{1,14}$/;

// Normalize free-form phone input to E.164. Mirrors backend logic in fusionRouter.
function normalizeMobile(input: string): string {
  const trimmed = input.trim();
  if (trimmed === '') return '';
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (hasPlus) return '+' + digits;
  if (digits.length === 10) return '+1' + digits;
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
  return trimmed;
}

const UserEdit: React.FC = () => {
  const { userInfo } = useFusionAuth();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [message, setMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    const fetchUserDetails = async (userId: string) => {
      if (!userId) return;
      try {
        const user = await getUserById(userId);
        setEmail(user.email);
        setFirstName(user.firstName);
        setLastName(user.lastName);
        setMobilePhone(user.mobilePhone ?? '');
      } catch (error) {
        console.error('Error fetching user details:', error);
        setMessage('Failed to fetch user details');
      }
    };

    if (userInfo && userInfo.sub) {
      fetchUserDetails(userInfo.sub);
    }
  }, [userInfo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPhoneError('');
    const normalizedPhone = normalizeMobile(mobilePhone);
    if (normalizedPhone !== '' && !E164.test(normalizedPhone)) {
      setPhoneError('Enter 10 digits (e.g. 867-555-1212) or international format with + (e.g. +442079460958).');
      return;
    }
    setMobilePhone(normalizedPhone);
    if (!userInfo?.sub) return;
    setMessage('Saving…');
    try {
      const user = await updateUser({
        id: userInfo.sub,
        firstName,
        lastName,
        mobilePhone: normalizedPhone,
      });
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setMobilePhone(user.mobilePhone ?? '');
      setMessage('Your account details were saved.');
    } catch (error: any) {
      console.error('Error updating user details:', error);
      setMessage(`Failed to update: ${error.message ?? 'unknown error'}`);
    }
  };

  return (
    <div className="useredit-container">
      <h2 className="useredit-title">Account details</h2>
      <form onSubmit={handleSubmit} className="useredit-form">
        <label className="useredit-label">
          Email
          <input
            className="useredit-input"
            type="email"
            value={email}
            disabled
            readOnly
          />
          <small>Email is managed by your administrator.</small>
        </label>

        <label className="useredit-label">
          First name
          <input
            className="useredit-input"
            type="text"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setMessage('* You have unsaved changes.');
            }}
          />
        </label>

        <label className="useredit-label">
          Last name
          <input
            className="useredit-input"
            type="text"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setMessage('* You have unsaved changes.');
            }}
          />
        </label>

        <label className="useredit-label">
          Mobile phone
          <input
            className="useredit-input"
            type="tel"
            value={mobilePhone}
            onChange={(e) => {
              setMobilePhone(e.target.value);
              setPhoneError('');
              setMessage('* You have unsaved changes.');
            }}
            placeholder="867-555-1212"
          />
          <small>10 digits or international format with + (saved as +18675551212).</small>
          {phoneError && <p className="useredit-error">{phoneError}</p>}
        </label>

        <button type="submit" className="useredit-button">
          Save
        </button>
      </form>

      {message && <p className="useredit-message">{message}</p>}
    </div>
  );
};

export default UserEdit;
